import React, { useState, useEffect, useMemo } from 'react';
import { apiFetch } from '../services/cloudflareService';
import {
    Eye, Monitor, Smartphone, MousePointerClick, TrendingUp, BarChart3,
    Megaphone, Clock, Star, RefreshCw, Calendar,
    ArrowUpRight, ArrowDownRight, ChevronRight, Activity, Building2, ChevronDown,
    Download, Image, Video, Type, Code, FileText, ChevronUp, ChevronsUpDown,
    Zap, Tv2, CheckCircle2, PauseCircle, XCircle
} from 'lucide-react';
import InfoTooltip from './InfoTooltip';

interface AdAnalyticsDashboardProps {
    theme: 'dark' | 'light';
    hospitalId?: string;
    groupId?: string;
    hospitals?: any[];
    adAssets?: any[];
}

interface AnalyticsData {
    period: number;
    kpis: {
        totalImpressions: number;
        tvImpressions: number;
        mobileImpressions: number;
        totalClicks: number;
        ctr: number;
        activeScreens: number;
    };
    perAd?: any[];
    dailyTrend?: any[];
    hourlyHeatmap?: any[];
    reviewFunnel?: {
        reviewsShown: number;
        reviewsSubmitted: number;
        fiveStarCount: number;
        googleClicks: number;
        conversionRate: number;
    };
    perHospital?: any[];
}

type SortKey = 'impressions' | 'clicks' | 'ctr' | 'tvCount' | 'mobileCount';
type SortDir = 'asc' | 'desc';

// ── Media Type Config ─────────────────────────────────────────────────────────
const MEDIA_TYPE_CONFIG: Record<string, { label: string; icon: React.FC<any>; color: string; bg: string; border: string }> = {
    BANNER_IMAGE: {
        label: 'Imagen',
        icon: Image,
        color: 'text-blue-400',
        bg: 'bg-blue-500/10',
        border: 'border-blue-500/20',
    },
    FULLSCREEN_SLIDE: {
        label: 'Imagen Full',
        icon: Image,
        color: 'text-indigo-400',
        bg: 'bg-indigo-500/10',
        border: 'border-indigo-500/20',
    },
    BANNER_VIDEO: {
        label: 'Video',
        icon: Video,
        color: 'text-rose-400',
        bg: 'bg-rose-500/10',
        border: 'border-rose-500/20',
    },
    BANNER_TEXT: {
        label: 'Texto',
        icon: Type,
        color: 'text-amber-400',
        bg: 'bg-amber-500/10',
        border: 'border-amber-500/20',
    },
    TICKER: {
        label: 'Ticker',
        icon: Type,
        color: 'text-orange-400',
        bg: 'bg-orange-500/10',
        border: 'border-orange-500/20',
    },
    TICKER_TEXT: {
        label: 'Ticker',
        icon: Type,
        color: 'text-orange-400',
        bg: 'bg-orange-500/10',
        border: 'border-orange-500/20',
    },
    BANNER_HTML: {
        label: 'HTML',
        icon: Code,
        color: 'text-emerald-400',
        bg: 'bg-emerald-500/10',
        border: 'border-emerald-500/20',
    },
    DEFAULT: {
        label: 'Media',
        icon: FileText,
        color: 'text-slate-400',
        bg: 'bg-slate-500/10',
        border: 'border-slate-500/20',
    },
};

const getMediaType = (type: string) => MEDIA_TYPE_CONFIG[type] || MEDIA_TYPE_CONFIG.DEFAULT;

const AdAnalyticsDashboard: React.FC<AdAnalyticsDashboardProps> = ({
    theme, hospitalId, groupId, hospitals = [], adAssets = []
}) => {
    const [data, setData] = useState<AnalyticsData | null>(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState(30);
    const [selectedHospitalId, setSelectedHospitalId] = useState<string | null>(null);
    const [showHospitalPicker, setShowHospitalPicker] = useState(false);
    const [sortKey, setSortKey] = useState<SortKey>('impressions');
    const [sortDir, setSortDir] = useState<SortDir>('desc');
    const [expandedAdId, setExpandedAdId] = useState<string | null>(null);
    const [typeFilter, setTypeFilter] = useState<string | null>(null);

    const t = useMemo(() => ({
        textWhite: theme === 'dark' ? 'text-white' : 'text-slate-900',
        textMain: theme === 'dark' ? 'text-slate-300' : 'text-slate-600',
        textMuted: theme === 'dark' ? 'text-slate-400' : 'text-slate-500',
        cardBg: theme === 'dark' ? 'bg-white/[0.02] backdrop-blur-2xl' : 'bg-white/70 backdrop-blur-2xl',
        cardBorder: theme === 'dark' ? 'border-white/[0.05]' : 'border-slate-200/60',
        cardShadow: theme === 'dark' ? 'shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]' : 'shadow-[inset_0_1px_0_rgba(255,255,255,1),_0_8px_30px_rgba(0,0,0,0.02)]',
        innerBg: theme === 'dark' ? 'bg-white/[0.02] border border-white/[0.05]' : 'bg-slate-50 border border-slate-100',
        tableBg: theme === 'dark' ? 'bg-black/20' : 'bg-white',
        tableHeader: theme === 'dark' ? 'bg-white/[0.03] border-b border-white/[0.06]' : 'bg-slate-50 border-b border-slate-200/60',
        tableRow: theme === 'dark' ? 'border-b border-white/[0.04] hover:bg-white/[0.03]' : 'border-b border-slate-100 hover:bg-slate-50/50',
        tableExpanded: theme === 'dark' ? 'bg-white/[0.02]' : 'bg-indigo-50/40',
        divider: theme === 'dark' ? 'border-white/[0.05]' : 'border-slate-200/60',
        pillBg: theme === 'dark' ? 'bg-white/5 border border-white/10' : 'bg-slate-100 border border-slate-200',
    }), [theme]);

    const cardClass = `${t.cardBg} rounded-2xl border ${t.cardBorder} ${t.cardShadow} relative overflow-hidden`;

    const formatNumber = (n: number) => {
        if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
        if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
        return n.toLocaleString('es-MX');
    };

    const effectiveHospitalId = selectedHospitalId || hospitalId;
    const useGroupEndpoint = groupId && !selectedHospitalId;

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                let url = '';
                if (useGroupEndpoint && groupId) {
                    url = `/api/ads/analytics/group/${groupId}?period=${period}`;
                } else if (effectiveHospitalId) {
                    url = `/api/ads/analytics/${effectiveHospitalId}?period=${period}`;
                }
                if (!url) { setLoading(false); return; }

                const res = await apiFetch(url, { credentials: 'include' });
                if (res.ok) {
                    setData(await res.json());
                } else {
                    setData(null);
                }
            } catch (err) {
                console.error('Error fetching ad analytics:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [effectiveHospitalId, useGroupEndpoint, groupId, period]);

    const getAdLabel = (adId: string) => {
        const asset = adAssets.find(a => a.id === adId);
        if (asset) {
            return asset.name || asset.title || asset.text || asset.imageAlt || asset.label || `${getMediaType(asset.type || '').label} — ${new Date(asset.createdAt || Date.now()).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })}`;
        }
        return adId.length > 16 ? adId.slice(0, 14) + '...' : adId;
    };

    const getAdPreview = (adId: string) => {
        const asset = adAssets.find(a => a.id === adId);
        return asset?.imageUrl || asset?.src || null;
    };

    const getAdStatus = (adId: string): { label: string; color: string; icon: React.FC<any> } => {
        const asset = adAssets.find(a => a.id === adId);
        if (!asset) return { label: 'Sin registro', color: theme === 'dark' ? 'text-slate-500 bg-slate-500/10 border-slate-500/20' : 'text-slate-400 bg-slate-100 border-slate-200', icon: XCircle };
        if (!asset.isActive) return { label: 'Pausado', color: theme === 'dark' ? 'text-amber-400 bg-amber-500/10 border-amber-500/20' : 'text-amber-600 bg-amber-50 border-amber-200', icon: PauseCircle };
        return { label: 'Activo', color: theme === 'dark' ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20' : 'text-emerald-600 bg-emerald-50 border-emerald-200', icon: CheckCircle2 };
    };

    const getHospitalName = (id: string) => {
        const h = hospitals.find((h: any) => h.id === id);
        return h?.name || id;
    };

    const activeVisualsCount = adAssets.filter(a => a.isActive).length;

    const selectedHospitalName = selectedHospitalId
        ? getHospitalName(selectedHospitalId)
        : groupId
            ? 'Todas las Sucursales'
            : (hospitalId ? getHospitalName(hospitalId) : 'Seleccionar Hospital');

    // ── Sorted + Filtered Table Data ─────────────────────────────────────
    const uniqueTypes = useMemo(() => {
        if (!data?.perAd) return [];
        const types = [...new Set(data.perAd.map((a: any) => a.adType))];
        return types;
    }, [data?.perAd]);

    const sortedPerAd = useMemo(() => {
        if (!data?.perAd) return [];
        let filtered = [...data.perAd];
        if (typeFilter) {
            filtered = filtered.filter((a: any) => a.adType === typeFilter);
        }
        return filtered.sort((a, b) => {
            const valA = a[sortKey] ?? 0;
            const valB = b[sortKey] ?? 0;
            return sortDir === 'desc' ? valB - valA : valA - valB;
        });
    }, [data?.perAd, sortKey, sortDir, typeFilter]);

    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir(d => d === 'desc' ? 'asc' : 'desc');
        } else {
            setSortKey(key);
            setSortDir('desc');
        }
    };

    const SortIcon = ({ col }: { col: SortKey }) => {
        if (sortKey !== col) return <ChevronsUpDown className={`w-3 h-3 ${t.textMuted} opacity-40`} />;
        return sortDir === 'desc'
            ? <ChevronDown className="w-3 h-3 text-indigo-400" />
            : <ChevronUp className="w-3 h-3 text-indigo-400" />;
    };

    const exportAdsCSV = () => {
        if (!data?.perAd || data.perAd.length === 0) return;
        const headers = 'Anuncio,Tipo de Media,Impresiones Total,Impresiones TV,Impresiones Móvil,Clicks,CTR (%)';
        const rows = data.perAd.map((ad: any) => {
            const label = getAdLabel(ad.adId).replace(/"/g, '""');
            const type = getMediaType(ad.adType).label;
            return `"${label}","${type}",${ad.impressions},${ad.tvCount},${ad.mobileCount},${ad.clicks || 0},${ad.ctr || 0}`;
        });
        const blob = new Blob(['\uFEFF' + headers + '\n' + rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `analytics_media_${period}dias_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (loading) {
        return (
            <div className={`${cardClass} p-6`}>
                <div className="space-y-3 animate-pulse">
                    <div className={`h-6 w-48 rounded-lg ${theme === 'dark' ? 'bg-white/5' : 'bg-slate-200'}`} />
                    <div className="grid grid-cols-4 gap-3">
                        {[1,2,3,4].map(i => <div key={i} className={`h-24 rounded-xl ${theme === 'dark' ? 'bg-white/5' : 'bg-slate-100'}`} />)}
                    </div>
                    <div className={`h-48 rounded-xl ${theme === 'dark' ? 'bg-white/5' : 'bg-slate-100'}`} />
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className={`${cardClass} text-center py-16 p-6`}>
                <div className={`p-5 rounded-2xl ${theme === 'dark' ? 'bg-violet-500/10 border border-violet-500/20' : 'bg-violet-50 border border-violet-200'} inline-block mb-5`}>
                    <Megaphone className="w-10 h-10 text-violet-400" />
                </div>
                <h3 className={`text-lg font-bold ${t.textWhite} mb-2`}>Sin datos de publicidad</h3>
                <p className={`text-sm ${t.textMuted} max-w-md mx-auto`}>
                    Los datos aparecerán automáticamente cuando haya contenido activo en las pantallas del hospital.
                </p>
            </div>
        );
    }

    const { kpis, perAd = [], dailyTrend = [], hourlyHeatmap = [], reviewFunnel, perHospital = [] } = data;
    const maxHourly = Math.max(...hourlyHeatmap.map((h: any) => h.impressions), 1);

    const periodOptions = [
        { value: 7, label: '7d' },
        { value: 14, label: '14d' },
        { value: 30, label: '30d' },
        { value: 60, label: '60d' },
        { value: 90, label: '90d' },
    ];

    return (
        <div className="space-y-6">

            {/* ── HEADER ─────────────────────────────────────────────────── */}
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <h2 className={`text-xl font-black ${t.textWhite} tracking-tight flex items-center gap-3`}>
                        <div className="p-2 bg-violet-500/10 text-violet-400 rounded-xl border border-violet-500/20">
                            <Megaphone className="w-5 h-5" />
                        </div>
                        Media Performance
                    </h2>
                    <p className={`text-xs ${t.textMuted} font-medium mt-1.5`}>
                        Rendimiento de contenido en pantallas y dispositivos móviles
                    </p>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                    {/* Export */}
                    <button
                        onClick={exportAdsCSV}
                        disabled={perAd.length === 0}
                        title="Exportar a CSV"
                        className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold transition-all border ${theme === 'dark' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/20 disabled:opacity-30' : 'bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100 disabled:opacity-30'}`}
                    >
                        <Download className="w-3.5 h-3.5" /> CSV
                    </button>

                    {/* Hospital Selector */}
                    {hospitals.length > 1 && (
                        <div className="relative">
                            <button
                                onClick={() => setShowHospitalPicker(!showHospitalPicker)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-xl border ${t.cardBorder} ${t.cardBg} text-sm font-bold ${t.textWhite} hover:opacity-80 transition-all`}
                            >
                                <Building2 className="w-4 h-4 text-indigo-400" />
                                <span className="max-w-[160px] truncate">{selectedHospitalName}</span>
                                <ChevronDown className={`w-3.5 h-3.5 ${t.textMuted} transition-transform ${showHospitalPicker ? 'rotate-180' : ''}`} />
                            </button>
                            {showHospitalPicker && (
                                <>
                                    <div className="fixed inset-0 z-40" onClick={() => setShowHospitalPicker(false)} />
                                    <div className={`absolute right-0 top-full mt-2 z-50 min-w-[220px] rounded-xl border ${t.cardBorder} ${theme === 'dark' ? 'bg-slate-900' : 'bg-white'} shadow-2xl py-1 max-h-60 overflow-y-auto`}>
                                        {groupId && (
                                            <>
                                                <button
                                                    onClick={() => { setSelectedHospitalId(null); setShowHospitalPicker(false); }}
                                                    className={`w-full text-left px-4 py-2.5 text-sm font-bold transition-colors flex items-center gap-2 ${!selectedHospitalId ? 'bg-indigo-600 text-white' : `${t.textMain} ${theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}`}
                                                >
                                                    <Activity className="w-4 h-4" /> Todas las Sucursales
                                                </button>
                                                <div className={`h-px ${t.divider} my-1`} />
                                            </>
                                        )}
                                        {hospitals.map((h: any) => (
                                            <button
                                                key={h.id}
                                                onClick={() => { setSelectedHospitalId(h.id); setShowHospitalPicker(false); }}
                                                className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors truncate ${selectedHospitalId === h.id ? 'bg-indigo-600 text-white' : `${t.textMain} ${theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}`}
                                            >
                                                {h.name}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* Period Selector */}
                    <div className={`flex ${t.cardBg} p-1 rounded-xl border ${t.cardBorder} gap-0.5`}>
                        {periodOptions.map(opt => (
                            <button
                                key={opt.value}
                                onClick={() => setPeriod(opt.value)}
                                className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all ${period === opt.value ? 'bg-indigo-600 text-white shadow-[0_0_15px_rgba(79,70,229,0.3)]' : `${t.textMuted} hover:${theme === 'dark' ? 'bg-white/5' : 'bg-slate-100'}`}`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── KPI STRIP ──────────────────────────────────────────────── */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {[
                    {
                        label: 'Impresiones',
                        value: kpis.totalImpressions,
                        icon: Eye,
                        color: 'violet',
                        tooltip: 'Número de veces que cualquier contenido fue mostrado en pantalla o celular.',
                    },
                    {
                        label: 'TV',
                        value: kpis.tvImpressions,
                        icon: Monitor,
                        color: 'cyan',
                        sub: kpis.activeScreens > 0 ? `${kpis.activeScreens} pantallas` : undefined,
                        tooltip: 'Impresiones desde pantallas de TV del hospital.',
                    },
                    {
                        label: 'Móvil',
                        value: kpis.mobileImpressions,
                        icon: Smartphone,
                        color: 'blue',
                        tooltip: 'Impresiones desde celulares de familiares (FamilyView).',
                    },
                    {
                        label: 'Clicks',
                        value: kpis.totalClicks,
                        icon: MousePointerClick,
                        color: 'amber',
                        badge: kpis.ctr > 0 ? `${kpis.ctr}% CTR` : undefined,
                        tooltip: 'Toques en banners desde móvil. CTR = Clicks ÷ Impresiones.',
                    },
                    {
                        label: 'Visuales Activos',
                        value: activeVisualsCount,
                        icon: Zap,
                        color: 'emerald',
                        tooltip: 'Número de contenidos publicitarios activos (encendidos) en este momento.',
                    },
                ].map(kpi => {
                    const Icon = kpi.icon;
                    const colorMap: Record<string, string> = {
                        violet: theme === 'dark' ? 'bg-violet-500/10 text-violet-400 border-violet-500/20' : 'bg-violet-50 text-violet-600 border-violet-200',
                        cyan: theme === 'dark' ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20' : 'bg-cyan-50 text-cyan-600 border-cyan-200',
                        blue: theme === 'dark' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' : 'bg-blue-50 text-blue-600 border-blue-200',
                        amber: theme === 'dark' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' : 'bg-amber-50 text-amber-600 border-amber-200',
                        emerald: theme === 'dark' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-emerald-50 text-emerald-600 border-emerald-200',
                    };
                    return (
                        <div key={kpi.label} className={`${cardClass} p-5 group`}>
                            <div className={`p-2 rounded-lg w-fit mb-3 border ${colorMap[kpi.color]} group-hover:scale-110 transition-transform`}>
                                <Icon className="w-4 h-4" />
                            </div>
                            <div className="flex items-end gap-2">
                                <p className={`text-2xl font-black ${t.textWhite} tracking-tight font-mono`}>{formatNumber(kpi.value)}</p>
                                {kpi.badge && (
                                    <span className="text-[10px] font-bold text-amber-400 bg-amber-500/10 border border-amber-500/20 px-1.5 py-0.5 rounded-full mb-0.5">
                                        {kpi.badge}
                                    </span>
                                )}
                            </div>
                            <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest mt-1 flex items-center gap-1`}>
                                {kpi.label}
                                <InfoTooltip theme={theme} text={kpi.tooltip} />
                            </p>
                            {kpi.sub && (
                                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full mt-1 inline-block ${theme === 'dark' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-emerald-50 text-emerald-600'}`}>
                                    {kpi.sub}
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* ── META-STYLE MEDIA TABLE ──────────────────────────────────── */}
            <div className={`${cardClass}`}>
                <div className="p-5 pb-3 flex items-center justify-between flex-wrap gap-3 border-b border-white/[0.05]">
                    <h3 className={`text-sm font-bold uppercase tracking-widest ${t.textWhite} flex items-center gap-2`}>
                        <BarChart3 className="w-4 h-4 text-violet-400" /> Rendimiento por Contenido
                    </h3>
                    <div className="flex items-center gap-2 flex-wrap">
                        {/* Type filter chips */}
                        {uniqueTypes.length > 1 && (
                            <div className="flex items-center gap-1 flex-wrap">
                                <button
                                    onClick={() => setTypeFilter(null)}
                                    className={`px-2.5 py-1 rounded-full text-[10px] font-bold transition-all border ${
                                        !typeFilter
                                            ? (theme === 'dark' ? 'bg-indigo-600 text-white border-indigo-500' : 'bg-indigo-600 text-white border-indigo-600')
                                            : `${t.pillBg} ${t.textMuted}`
                                    }`}
                                >
                                    Todos
                                </button>
                                {uniqueTypes.map(type => {
                                    const mt = getMediaType(type);
                                    const MediaIcon = mt.icon;
                                    return (
                                        <button
                                            key={type}
                                            onClick={() => setTypeFilter(typeFilter === type ? null : type)}
                                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold transition-all border ${
                                                typeFilter === type
                                                    ? `${mt.bg} ${mt.color} ${mt.border}`
                                                    : `${t.pillBg} ${t.textMuted}`
                                            }`}
                                        >
                                            <MediaIcon className="w-2.5 h-2.5" />
                                            {mt.label}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${t.pillBg} ${t.textMuted}`}>
                            {sortedPerAd.length} item{sortedPerAd.length !== 1 ? 's' : ''}
                        </span>
                    </div>
                </div>

                {sortedPerAd.length === 0 ? (
                    <div className="p-10 text-center">
                        <Megaphone className={`w-8 h-8 ${t.textMuted} mx-auto mb-3 opacity-40`} />
                        <p className={`text-sm ${t.textMuted}`}>Sin datos para este periodo</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            {/* Column Headers */}
                            <thead>
                                <tr className={t.tableHeader}>
                                    <th className={`text-left py-3 px-5 text-[10px] font-black uppercase tracking-widest ${t.textMuted} w-8`}>#</th>
                                    <th className={`text-left py-3 px-4 text-[10px] font-black uppercase tracking-widest ${t.textMuted}`}>Contenido</th>
                                    <th className={`text-left py-3 px-3 text-[10px] font-black uppercase tracking-widest ${t.textMuted}`}>Tipo</th>
                                    <th className={`text-left py-3 px-3 text-[10px] font-black uppercase tracking-widest ${t.textMuted}`}>Estado</th>
                                    {[
                                        { key: 'impressions' as SortKey, label: 'Impresiones' },
                                        { key: 'tvCount' as SortKey, label: 'TV' },
                                        { key: 'mobileCount' as SortKey, label: 'Móvil' },
                                        { key: 'clicks' as SortKey, label: 'Clicks' },
                                        { key: 'ctr' as SortKey, label: 'CTR' },
                                    ].map(col => (
                                        <th key={col.key} className="py-3 px-3">
                                            <button
                                                onClick={() => handleSort(col.key)}
                                                className={`flex items-center gap-1 text-[10px] font-black uppercase tracking-widest transition-colors ${sortKey === col.key ? 'text-indigo-400' : t.textMuted} hover:text-indigo-400`}
                                            >
                                                {col.label} <SortIcon col={col.key} />
                                            </button>
                                        </th>
                                    ))}
                                    <th className="w-10" />
                                </tr>
                            </thead>

                            <tbody>
                                {sortedPerAd.map((ad: any, i: number) => {
                                    const mediaType = getMediaType(ad.adType);
                                    const MediaIcon = mediaType.icon;
                                    const preview = getAdPreview(ad.adId);
                                    const isExpanded = expandedAdId === ad.adId;
                                    const tvPct = ad.impressions > 0 ? Math.round((ad.tvCount / ad.impressions) * 100) : 0;
                                    const mobPct = 100 - tvPct;
                                    const status = getAdStatus(ad.adId);
                                    const StatusIcon = status.icon;

                                    return (
                                        <React.Fragment key={ad.adId}>
                                            <tr
                                                className={`${t.tableRow} transition-colors cursor-pointer group/row`}
                                                onClick={() => setExpandedAdId(isExpanded ? null : ad.adId)}
                                            >
                                                {/* Rank */}
                                                <td className="py-3 px-5">
                                                    <span className={`text-[11px] font-black font-mono ${i < 3 ? ['text-amber-400', 'text-slate-400', 'text-orange-400'][i] : t.textMuted}`}>
                                                        {i + 1}
                                                    </span>
                                                </td>

                                                {/* Preview + Name */}
                                                <td className="py-3 px-4 max-w-[200px]">
                                                    <div className="flex items-center gap-3">
                                                        {preview ? (
                                                            <img
                                                                src={preview}
                                                                alt=""
                                                                className="w-10 h-7 rounded-md object-cover flex-shrink-0 border border-white/10"
                                                            />
                                                        ) : (
                                                            <div className={`w-10 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${mediaType.bg} ${mediaType.border} border`}>
                                                                <MediaIcon className={`w-3.5 h-3.5 ${mediaType.color}`} />
                                                            </div>
                                                        )}
                                                        <p className={`text-sm font-semibold ${t.textWhite} truncate`} title={getAdLabel(ad.adId)}>
                                                            {getAdLabel(ad.adId)}
                                                        </p>
                                                    </div>
                                                </td>

                                                {/* Type Badge */}
                                                <td className="py-3 px-3">
                                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold border ${mediaType.bg} ${mediaType.color} ${mediaType.border}`}>
                                                        <MediaIcon className="w-2.5 h-2.5" />
                                                        {mediaType.label}
                                                    </span>
                                                </td>

                                                {/* Status Badge */}
                                                <td className="py-3 px-3">
                                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${status.color}`}>
                                                        <StatusIcon className="w-2.5 h-2.5" />
                                                        {status.label}
                                                    </span>
                                                </td>

                                                {/* Impressions */}
                                                <td className="py-3 px-3">
                                                    <span className={`font-black font-mono text-sm ${t.textWhite}`}>{formatNumber(ad.impressions)}</span>
                                                </td>

                                                {/* TV */}
                                                <td className="py-3 px-3">
                                                    <div className="flex items-center gap-1.5">
                                                        <Monitor className="w-3 h-3 text-cyan-400 flex-shrink-0" />
                                                        <span className={`font-mono text-sm ${t.textMain}`}>{formatNumber(ad.tvCount)}</span>
                                                    </div>
                                                </td>

                                                {/* Mobile */}
                                                <td className="py-3 px-3">
                                                    <div className="flex items-center gap-1.5">
                                                        <Smartphone className="w-3 h-3 text-blue-400 flex-shrink-0" />
                                                        <span className={`font-mono text-sm ${t.textMain}`}>{formatNumber(ad.mobileCount)}</span>
                                                    </div>
                                                </td>

                                                {/* Clicks */}
                                                <td className="py-3 px-3">
                                                    <span className={`font-mono text-sm ${ad.clicks > 0 ? 'text-amber-400 font-bold' : t.textMuted}`}>
                                                        {formatNumber(ad.clicks || 0)}
                                                    </span>
                                                </td>

                                                {/* CTR */}
                                                <td className="py-3 px-3">
                                                    {ad.ctr > 0 ? (
                                                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded-full">
                                                            {ad.ctr}%
                                                        </span>
                                                    ) : (
                                                        <span className={`text-xs ${t.textMuted}`}>—</span>
                                                    )}
                                                </td>

                                                {/* Expand toggle */}
                                                <td className="py-3 px-4">
                                                    <ChevronDown className={`w-3.5 h-3.5 ${t.textMuted} transition-transform ${isExpanded ? 'rotate-180' : ''} group-hover/row:text-indigo-400`} />
                                                </td>
                                            </tr>

                                            {/* Expanded row */}
                                            {isExpanded && (
                                                <tr className={t.tableExpanded}>
                                                    <td colSpan={10} className="px-6 py-5">
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                                            {/* Preview */}
                                                            <div className={`${theme === 'dark' ? 'bg-white/[0.03] border border-white/[0.05]' : 'bg-white border border-slate-200'} rounded-xl p-4 flex flex-col items-center justify-center`}>
                                                                <p className={`text-[10px] font-black uppercase tracking-widest ${t.textMuted} mb-3 self-start`}>Vista Previa</p>
                                                                {preview ? (
                                                                    <img src={preview} alt={getAdLabel(ad.adId)} className="w-full max-h-32 object-contain rounded-lg" />
                                                                ) : (
                                                                    <div className={`w-full h-24 rounded-lg flex items-center justify-center ${mediaType.bg} ${mediaType.border} border`}>
                                                                        <MediaIcon className={`w-8 h-8 ${mediaType.color}`} />
                                                                    </div>
                                                                )}
                                                                <p className={`text-xs font-bold ${t.textWhite} mt-2 text-center`}>{getAdLabel(ad.adId)}</p>
                                                            </div>

                                                            {/* TV vs Mobile Split */}
                                                            <div className={`${theme === 'dark' ? 'bg-white/[0.03] border border-white/[0.05]' : 'bg-white border border-slate-200'} rounded-xl p-4`}>
                                                                <p className={`text-[10px] font-black uppercase tracking-widest ${t.textMuted} mb-3`}>Split de Plataforma</p>
                                                                <div className="space-y-2">
                                                                    <div>
                                                                        <div className="flex justify-between mb-1">
                                                                            <span className={`text-xs font-bold text-cyan-400 flex items-center gap-1`}><Monitor className="w-3 h-3" /> TV</span>
                                                                            <span className={`text-xs font-bold ${t.textWhite}`}>{tvPct}%</span>
                                                                        </div>
                                                                        <div className={`h-2 rounded-full ${theme === 'dark' ? 'bg-white/[0.05]' : 'bg-slate-100'}`}>
                                                                            <div className="h-full rounded-full bg-cyan-500 transition-all" style={{ width: `${tvPct}%` }} />
                                                                        </div>
                                                                    </div>
                                                                    <div>
                                                                        <div className="flex justify-between mb-1">
                                                                            <span className={`text-xs font-bold text-blue-400 flex items-center gap-1`}><Smartphone className="w-3 h-3" /> Móvil</span>
                                                                            <span className={`text-xs font-bold ${t.textWhite}`}>{mobPct}%</span>
                                                                        </div>
                                                                        <div className={`h-2 rounded-full ${theme === 'dark' ? 'bg-white/[0.05]' : 'bg-slate-100'}`}>
                                                                            <div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${mobPct}%` }} />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="mt-3 space-y-1.5">
                                                                    {[
                                                                        { label: 'Total Impresiones', value: formatNumber(ad.impressions), color: 'text-violet-400' },
                                                                        { label: 'Clicks', value: formatNumber(ad.clicks || 0), color: 'text-amber-400' },
                                                                        { label: 'CTR', value: ad.ctr > 0 ? `${ad.ctr}%` : '—', color: 'text-emerald-400' },
                                                                    ].map(m => (
                                                                        <div key={m.label} className="flex justify-between items-center">
                                                                            <span className={`text-xs ${t.textMuted}`}>{m.label}</span>
                                                                            <span className={`text-sm font-black font-mono ${m.color}`}>{m.value}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>

                                                            {/* Screens Attribution */}
                                                            <div className={`${theme === 'dark' ? 'bg-white/[0.03] border border-white/[0.05]' : 'bg-white border border-slate-200'} rounded-xl p-4`}>
                                                                <p className={`text-[10px] font-black uppercase tracking-widest ${t.textMuted} mb-3 flex items-center gap-1.5`}>
                                                                    <Tv2 className="w-3 h-3" /> Pantallas donde se vio
                                                                </p>
                                                                {(ad.screens && ad.screens.length > 0) ? (
                                                                    <div className="space-y-1.5">
                                                                        {ad.screens.map((s: any, idx: number) => (
                                                                            <div key={idx} className="flex justify-between items-center">
                                                                                <span className={`text-xs font-medium ${t.textMain} truncate flex items-center gap-1.5`}>
                                                                                    <Monitor className="w-3 h-3 text-cyan-400 flex-shrink-0" />
                                                                                    {s.screenId.length > 20 ? s.screenId.slice(0, 18) + '...' : s.screenId}
                                                                                </span>
                                                                                <span className={`text-xs font-bold font-mono ${t.textWhite}`}>{formatNumber(s.impressions)}</span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                ) : (
                                                                    <p className={`text-xs ${t.textMuted} italic`}>Sin datos de pantalla</p>
                                                                )}
                                                            </div>

                                                            {/* ID + Status info */}
                                                            <div className={`${theme === 'dark' ? 'bg-white/[0.03] border border-white/[0.05]' : 'bg-white border border-slate-200'} rounded-xl p-4`}>
                                                                <p className={`text-[10px] font-black uppercase tracking-widest ${t.textMuted} mb-3`}>Identificación</p>
                                                                <div className="space-y-2">
                                                                    <div>
                                                                        <p className={`text-[10px] ${t.textMuted} mb-0.5`}>Estado</p>
                                                                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold border ${status.color}`}>
                                                                            <StatusIcon className="w-3 h-3" />
                                                                            {status.label}
                                                                        </span>
                                                                    </div>
                                                                    <div>
                                                                        <p className={`text-[10px] ${t.textMuted} mb-0.5`}>ID del contenido</p>
                                                                        <p className={`text-[11px] font-mono ${t.textWhite} truncate`}>{ad.adId}</p>
                                                                    </div>
                                                                    <div>
                                                                        <p className={`text-[10px] ${t.textMuted} mb-0.5`}>Tipo técnico</p>
                                                                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold border ${getMediaType(ad.adType).bg} ${getMediaType(ad.adType).color} ${getMediaType(ad.adType).border}`}>
                                                                            {ad.adType}
                                                                        </span>
                                                                    </div>
                                                                    {ad.lastSeen && (
                                                                        <div>
                                                                            <p className={`text-[10px] ${t.textMuted} mb-0.5`}>Última actividad</p>
                                                                            <p className={`text-[11px] font-mono ${t.textWhite}`}>
                                                                                {new Date(ad.lastSeen).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                                            </p>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* ── HOURLY HEATMAP ──────────────────────────────────── */}
            {hourlyHeatmap.length > 0 && (
                <div className={`${cardClass} p-6`}>
                    <h3 className={`text-sm font-bold uppercase tracking-widest ${t.textWhite} mb-1 flex items-center gap-2`}>
                        <Clock className="w-4 h-4 text-cyan-400" /> Pico de Audiencia por Hora
                    </h3>
                    <p className={`text-[10px] ${t.textMuted} mb-5`}>¿Cuándo se ve más tu contenido?</p>
                    <div className="flex items-end gap-px h-28">
                        {Array.from({ length: 24 }, (_, hour) => {
                            const entry = hourlyHeatmap.find((h: any) => h.hour === hour);
                            const impressions = entry?.impressions || 0;
                            const height = maxHourly > 0 ? Math.max((impressions / maxHourly) * 100, impressions > 0 ? 4 : 0) : 0;
                            const isPeak = impressions === maxHourly && impressions > 0;
                            return (
                                <div key={hour} className="flex-1 flex flex-col items-center gap-1" title={`${hour}:00 — ${formatNumber(impressions)} imp.`}>
                                    <div
                                        className={`w-full rounded-t-sm transition-all duration-500 ${isPeak ? 'bg-violet-500' : impressions > 0 ? (theme === 'dark' ? 'bg-violet-500/40' : 'bg-violet-500/30') : (theme === 'dark' ? 'bg-white/[0.03]' : 'bg-slate-100')}`}
                                        style={{ height: `${height}%`, minHeight: '2px' }}
                                    />
                                    {(hour % 6 === 0) && (
                                        <span className={`text-[8px] font-mono ${t.textMuted}`}>{hour}h</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ── PER-HOSPITAL TABLE (Group mode) ───────────────────────── */}
            {perHospital.length > 0 && (
                <div className={`${cardClass} p-6`}>
                    <h3 className={`text-sm font-bold uppercase tracking-widest ${t.textWhite} mb-4 flex items-center gap-2`}>
                        <Activity className="w-4 h-4 text-indigo-400" /> Desglose por Sucursal
                    </h3>
                    <div className="space-y-2">
                        {perHospital.map((h: any) => {
                            const maxImp = Math.max(...perHospital.map((hh: any) => hh.impressions), 1);
                            const pct = Math.max((h.impressions / maxImp) * 100, 4);
                            return (
                                <button
                                    key={h.hospitalId}
                                    onClick={() => setSelectedHospitalId(h.hospitalId)}
                                    className={`w-full flex items-center gap-4 p-4 rounded-xl ${t.innerBg} hover:ring-2 hover:ring-indigo-500/30 transition-all text-left group`}
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between mb-1.5">
                                            <p className={`text-sm font-bold ${t.textWhite} truncate group-hover:text-indigo-400 transition-colors`}>{h.name || getHospitalName(h.hospitalId)}</p>
                                            <p className={`text-sm font-black font-mono ${t.textWhite} flex-shrink-0 ml-4`}>{formatNumber(h.impressions)}</p>
                                        </div>
                                        <div className={`h-1.5 rounded-full ${theme === 'dark' ? 'bg-white/[0.05]' : 'bg-slate-100'}`}>
                                            <div className="h-full rounded-full bg-indigo-500 transition-all duration-700" style={{ width: `${pct}%` }} />
                                        </div>
                                        <p className={`text-[10px] ${t.textMuted} mt-1.5`}>
                                            TV: {formatNumber(h.tvCount)} • Móvil: {formatNumber(h.mobileCount)} • {h.screens} pantalla{h.screens !== 1 ? 's' : ''}
                                        </p>
                                    </div>
                                    <ChevronRight className={`w-4 h-4 ${t.textMuted} flex-shrink-0 group-hover:text-indigo-400 transition-colors`} />
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ── DAILY TREND ───────────────────────────────────────────── */}
            {dailyTrend.length > 1 && (
                <div className={`${cardClass} p-6`}>
                    <h3 className={`text-sm font-bold uppercase tracking-widest ${t.textWhite} mb-4 flex items-center gap-2`}>
                        <TrendingUp className="w-4 h-4 text-emerald-400" /> Tendencia Diaria
                    </h3>
                    <div className="flex items-end gap-0.5 h-20">
                        {dailyTrend.map((d: any) => {
                            const maxDaily = Math.max(...dailyTrend.map((dd: any) => dd.impressions), 1);
                            const height = Math.max((d.impressions / maxDaily) * 100, d.impressions > 0 ? 4 : 0);
                            return (
                                <div
                                    key={d.date}
                                    className={`flex-1 rounded-t-sm transition-all ${theme === 'dark' ? 'bg-emerald-500/40 hover:bg-emerald-500/70' : 'bg-emerald-500/30 hover:bg-emerald-500/60'}`}
                                    style={{ height: `${height}%`, minHeight: '2px' }}
                                    title={`${d.date}: ${formatNumber(d.impressions)} imp., ${formatNumber(d.clicks)} clicks`}
                                />
                            );
                        })}
                    </div>
                    <div className="flex justify-between mt-2">
                        <span className={`text-[9px] font-mono ${t.textMuted}`}>{dailyTrend[0]?.date}</span>
                        <span className={`text-[9px] font-mono ${t.textMuted}`}>{dailyTrend[dailyTrend.length - 1]?.date}</span>
                    </div>
                </div>
            )}

            {/* ── EMPTY STATE ───────────────────────────────────────────── */}
            {kpis.totalImpressions === 0 && (
                <div className={`${cardClass} text-center py-14 p-6`}>
                    <div className={`p-5 rounded-2xl ${theme === 'dark' ? 'bg-violet-500/10 border border-violet-500/20' : 'bg-violet-50 border border-violet-200'} inline-block mb-5`}>
                        <Megaphone className="w-10 h-10 text-violet-400" />
                    </div>
                    <h3 className={`text-lg font-bold ${t.textWhite} mb-2`}>Tracking activado</h3>
                    <p className={`text-sm ${t.textMuted} max-w-md mx-auto`}>
                        Las impresiones se registrarán automáticamente cuando tu contenido se muestre en las pantallas de TV y en los dispositivos móviles de los familiares.
                    </p>
                </div>
            )}
        </div>
    );
};

export default AdAnalyticsDashboard;
